import { useLocation } from "wouter";
import { Zap, Sparkles, MessageCircleIcon, UsersIcon, Settings } from 'lucide-react';
import { cn } from "@/lib/utils";
import { useApp } from "@/lib/context";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { socketService } from "@/lib/socket-service";
import { getToken } from "@/lib/token-storage";
import { useToast } from "@/hooks/use-toast";
import { useDesktopShell } from "@/hooks/use-desktop-shell";

// Preload delle immagini animate per eliminare il lag
const preloadImages = () => {
	const imageUrls = [
		"/assets/images/emoji/crystal-ball_1f52e.webp",
		"/assets/images/emoji/compass_1f9ed.webp",
		"/assets/images/emoji/admission-tickets_1f39f-fe0f.webp",
		"/assets/images/emoji/performing-arts_1f3ad.webp",
		"/assets/images/emoji/speech-balloon_1f4ac.webp"
	];

	imageUrls.forEach(url => {
		const img = new Image();
		img.src = url;
	});
};

export default function Navbar() {
	const [location, setLocation] = useLocation();
	const { user } = useApp();
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
	const [hasPlayedAnimation, setHasPlayedAnimation] = useState(false); // Stato per tracciare se l'animazione è già stata riprodotta
	const [isInConversation, setIsInConversation] = useState(false); // Track if user is in a chat conversation
	const [isInWall, setIsInWall] = useState(false); // Track if user is in wall section
	const queryClient = useQueryClient(); // Ottieni queryClient per invalidare le query
	const { toast } = useToast();
	const desktopShellEnabled = useDesktopShell();

	// Preload immagini al mount del componente
	useEffect(() => {
		preloadImages();
	}, []);

	// Rileva apertura/chiusura tastiera con detection più rapida e accurata
	useEffect(() => {
		// Salva l'altezza iniziale della viewport
		let initialViewportHeight = window.visualViewport?.height || window.innerHeight;

		const handleViewportChange = () => {
			const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
			const heightDifference = initialViewportHeight - currentViewportHeight;

			// Soglia più sensibile per detection rapida (100px invece di 150px)
			const keyboardThreshold = 100;
			const keyboardIsOpen = heightDifference > keyboardThreshold;

			// Aggiorna immediatamente lo stato per evitare flickering
			setIsKeyboardOpen(keyboardIsOpen);
		};

		// Rileva anche focus/blur su input per detection preventiva
		const handleFocusIn = (e: FocusEvent) => {
			const target = e.target as HTMLElement;
			// NON nascondere la navbar se l'input è quello del Wall
			const isWallInput = target.hasAttribute('data-wall-input');
			if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') && !isWallInput) {
				// Pre-nasconde la navbar quando un input riceve focus
				// Questo previene il flickering su dispositivi con keyboard animation lenta
				setTimeout(() => setIsKeyboardOpen(true), 50);
			}
		};

		const handleFocusOut = (e: FocusEvent) => {
			const target = e.target as HTMLElement;
			if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true')) {
				// Aspetta un momento prima di rimostrare la navbar per evitare flickering
				// durante il cambio di focus tra input
				setTimeout(() => {
					const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
					const heightDifference = initialViewportHeight - currentViewportHeight;
					const keyboardIsOpen = heightDifference > 100;
					setIsKeyboardOpen(keyboardIsOpen);
				}, 100);
			}
		};

		// Usa Visual Viewport API se disponibile (più accurato per tastiere virtuali)
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', handleViewportChange);

			// Aggiunge listener per focus/blur per detection preventiva
			document.addEventListener('focusin', handleFocusIn);
			document.addEventListener('focusout', handleFocusOut);

			return () => {
				window.visualViewport?.removeEventListener('resize', handleViewportChange);
				document.removeEventListener('focusin', handleFocusIn);
				document.removeEventListener('focusout', handleFocusOut);
			};
		} else {
			// Fallback per browser che non supportano Visual Viewport API
			window.addEventListener('resize', handleViewportChange);
			document.addEventListener('focusin', handleFocusIn);
			document.addEventListener('focusout', handleFocusOut);

			return () => {
				window.removeEventListener('resize', handleViewportChange);
				document.removeEventListener('focusin', handleFocusIn);
				document.removeEventListener('focusout', handleFocusOut);
			};
		}
	}, []);

	// Query per contare i messaggi non letti
	const { data: unreadData } = useQuery({
		queryKey: ['/api/chat/unread-count'],
		queryFn: async () => {
			try {
				const response = await apiRequest('GET', '/api/chat/conversations');
				if (!Array.isArray(response)) return { hasUnread: false, unreadCount: 0 };

				const unreadCount = response.reduce(
					(sum: number, conv: any) => sum + (conv.unreadCount || 0),
					0
				);
				return { hasUnread: unreadCount > 0, unreadCount };
			} catch (error) {
				console.error('[NAVBAR] Error fetching unread count:', error);
				return { hasUnread: false, unreadCount: 0 };
			}
		},
		enabled: !!user,
		staleTime: 10 * 1000,
		gcTime: 30 * 1000,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		retry: 1,
		throwOnError: false,
	});

	const hasUnreadMessages = unreadData?.hasUnread || false;
	const unreadCount = unreadData?.unreadCount || 0;

	// Debug badge
	console.log('[NAVBAR] Badge debug:', {
		hasUnreadMessages,
		unreadData
	});

	// Listener per nuovi messaggi
	const handleNewMessage = (data: any) => {
		console.log('[NAVBAR] 💬 NUOVO MESSAGGIO VIA WEBSOCKET!', data);

		// Invalida le query per aggiornare il badge
		queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-count'] });
		queryClient.invalidateQueries({ queryKey: ['/api/chat/conversations'] });

		console.log('[NAVBAR] 💬 Badge chat aggiornato via WebSocket');
	};

	// Listen for conversation opened/closed events
	useEffect(() => {
		const handleConversationOpened = () => {
			console.log('[NAVBAR] 💬 Conversazione aperta, nascondo navbar');
			setIsInConversation(true);
		};

		const handleConversationClosed = () => {
			console.log('[NAVBAR] 💬 Conversazione chiusa, mostro navbar');
			setIsInConversation(false);
		};

		window.addEventListener('chatConversationOpened', handleConversationOpened);
		window.addEventListener('chatConversationClosed', handleConversationClosed);

		return () => {
			window.removeEventListener('chatConversationOpened', handleConversationOpened);
			window.removeEventListener('chatConversationClosed', handleConversationClosed);
		};
	}, []);

	// Listen for wall section changes to hide navbar (using layoutEffect to sync before paint)
	useLayoutEffect(() => {
		const handleWallSectionChange = (event: Event) => {
			const customEvent = event as CustomEvent;
			const { isInWall: wallState } = customEvent.detail;
			setIsInWall(wallState);
		};

		window.addEventListener('wallSectionChange', handleWallSectionChange);

		return () => {
			window.removeEventListener('wallSectionChange', handleWallSectionChange);
		};
	}, []);

	// 🔧 TELEGRAM MINI APP FIX: Registra listener IMMEDIATAMENTE (Socket.IO lo attiverà quando connesso)
	useEffect(() => {
		if (!user) return; // Non registrare se l'utente non è loggato

		socketService.on('new-chat-message', handleNewMessage);
		console.log('[NAVBAR] 📝 Listener new-chat-message registrato');

		// Pulizia dei listener quando il componente viene smontato
		return () => {
			socketService.off('new-chat-message', handleNewMessage);
			console.log('[NAVBAR] ❌ Listener new-chat-message rimosso');
		};
	}, [user, queryClient]); // Aggiunto user e queryClient alle dipendenze per assicurare che i listener siano gestiti correttamente

	const isActive = (path: string) => location === path;

	const handleChatClick = () => {
		setLocation("/chat");
		// Riproduci l'animazione solo se non è già stata riprodotta
		if (!hasPlayedAnimation) {
			setHasPlayedAnimation(true);
			// Qui potresti aggiungere la logica per far scattare l'animazione,
			// ad esempio cambiando uno stato che il NavButton usa per decidere quale icona mostrare.
			// Per ora, il cambio di stato è gestito da `isActive`
		}
	};

	const triggerHaptic = (intensity: 'light' | 'medium' = 'light') => {
		try {
			const tg = (window as any).Telegram?.WebApp;
			if (tg && tg.HapticFeedback) {
				tg.HapticFeedback.impactOccurred(intensity);
			}
		} catch (error) {
			console.log('[NAVBAR] Haptic feedback non disponibile');
		}
	};

	// Nuovo desktop shell: sidebar/topbar gestiti da DesktopShell, qui esci.
	if (desktopShellEnabled) {
		return null;
	}

	// Nav items config per riuso sidebar/bottom
	const navItems = [
		{ path: '/discover', altPath: '/', label: 'Discover', staticIcon: '/assets/images/staticemo/compass_1f9ed.png', animatedIcon: '/assets/images/emoji/compass_1f9ed.webp', glow: 'rgba(255, 255, 0, 0.3)' },
		{ path: '/wall', label: 'Wall', staticIcon: '/assets/images/staticemo/crystal-ball_1f52e.png', animatedIcon: '/assets/images/emoji/crystal-ball_1f52e.webp', glow: 'rgba(138, 43, 226, 0.3)' },
		{ path: '/events', label: 'Eventi', staticIcon: '/assets/images/staticemo/admission-tickets_1f39f-fe0f.png', animatedIcon: '/assets/images/emoji/admission-tickets_1f39f-fe0f.webp', glow: 'rgba(255, 215, 0, 0.3)' },
		{ path: '/groups', label: 'Gruppi', staticIcon: '/assets/images/staticemo/performing-arts_1f3ad.png', animatedIcon: '/assets/images/emoji/performing-arts_1f3ad.webp', glow: 'rgba(156, 39, 176, 0.3)' },
		{ path: '/chat', label: 'Chat', staticIcon: '/assets/images/staticemo/speech-balloon_1f4ac.png', animatedIcon: '/assets/images/emoji/speech-balloon_1f4ac.webp', glow: 'rgba(45, 165, 255, 0.3)', badge: unreadCount },
	];

	return (
		<>
			{/* Desktop Sidebar — hidden su mobile */}
			<aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-60 lg:z-40 tg-bg border-r border-white/[0.08]">
				{/* Logo / Brand */}
				<div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/[0.08]">
					<span
						className="w-8 h-8 rounded-[10px] inline-flex items-center justify-center text-lg flex-shrink-0"
						style={{
							background: 'linear-gradient(135deg, #2565eb, #8a2bb6)',
							boxShadow: '0 4px 12px rgba(37,101,235,0.35)',
						}}
					>
						🧭
					</span>
					<span className="text-[17px] font-bold text-white tracking-tight">kinkygram</span>
				</div>

				{/* Nav Items */}
				<nav className="flex-1 flex flex-col gap-0.5 p-3">
					<div className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/30">
						Naviga
					</div>
					{navItems.map((item) => {
						const active = isActive(item.path) || (item.altPath && isActive(item.altPath));
						const badgeCount = typeof item.badge === 'number' ? item.badge : 0;
						return (
							<button
								key={item.path}
								onClick={() => {
									try {
										if (window.Telegram?.WebApp?.HapticFeedback) {
											window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
										} else if ('vibrate' in navigator) {
											navigator.vibrate(30);
										}
									} catch {}
									if (item.path === '/chat') {
										handleChatClick();
									} else {
										setLocation(item.path);
									}
								}}
								className={cn(
									"relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150",
									active
										? "bg-white/[0.08] text-white font-semibold"
										: "text-white/50 hover:bg-white/[0.04] hover:text-white/80 font-medium"
								)}
							>
								{active && (
									<span className="absolute -left-3 top-2 bottom-2 w-[3px] rounded-full bg-[#2565eb]" />
								)}
								<div className="relative w-[26px] h-[26px] flex items-center justify-center flex-shrink-0">
									<img
										src={active ? item.animatedIcon : item.staticIcon}
										alt={item.label}
										className="w-[22px] h-[22px]"
										style={{ filter: `drop-shadow(0 0 4px ${item.glow})` }}
									/>
								</div>
								<span>{item.label}</span>
								{badgeCount > 0 && (
									<span className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-[#ec3942] text-white text-[11px] font-bold">
										{badgeCount > 99 ? '99+' : badgeCount}
									</span>
								)}
							</button>
						);
					})}
				</nav>

				{/* User pill */}
				<div className="p-3 border-t border-white/[0.08]">
					{user && (
						<button
							onClick={() => setLocation('/profile')}
							className="flex items-center gap-2.5 p-2.5 rounded-xl w-full text-left bg-white/[0.04] hover:bg-white/[0.08] transition-all"
						>
							<div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
								{(user as any).photoUrl || (user as any).photo_url ? (
									<img src={(user as any).photoUrl || (user as any).photo_url} alt="" className="w-full h-full object-cover" />
								) : (
									<span className="text-white/60 text-xs font-medium">
										{((user as any).username || '?')[0]?.toUpperCase()}
									</span>
								)}
							</div>
							<div className="flex-1 min-w-0">
								<p className="text-[13px] font-semibold text-white truncate">
									@{(user as any).username || 'profilo'}
								</p>
								{((user as any).profile_type || (user as any).category) && (
									<p className="text-[11px] text-white/40 truncate mt-0.5">
										{(user as any).profile_type || (user as any).category}
									</p>
								)}
							</div>
						</button>
					)}
				</div>
			</aside>

			{/* Mobile Bottom Bar — hidden su desktop */}
			{!isKeyboardOpen && !isInConversation && !isInWall && (
				<div className="fixed bottom-0 left-0 right-0 w-full max-w-md lg:hidden mx-auto z-10">
				<div className="kg-bottom-nav tg-bg standard-border-top flex items-center justify-around px-3 pb-2 shadow-lg pt-0 relative w-full">
				{/* Riga gradiente in cima alla navbar */}
				<div
					className="absolute -top-[1px] left-0 right-0 w-full h-[1px] pointer-events-none"
					style={{
						background: 'linear-gradient(to right, var(--tg-bg-color, #0c0c0c) 0%, rgba(255, 255, 255, 0.35) 50%, var(--tg-bg-color, #0c0c0c) 100%)'
					}}
				/>
				{/* Normal Mode - Show Navigation Buttons */}
				<div
					key="normal-mode"
					className="flex items-center justify-around w-full"
				>
					{/* Bussola → Discover */}
					<NavButton
					icon={
						<div className="relative w-7 h-7 flex items-center justify-center">
							<span 
								className={`text-2xl emoji-icon emoji-compass absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
									(isActive("/discover") || isActive("/")) ? 'opacity-0' : 'opacity-100'
								}`}
							>

							</span>
							<img 
								src="/assets/images/staticemo/compass_1f9ed.png"
								alt="Discover"
								className={`w-7 h-7 p-0.5 absolute inset-0 transition-opacity duration-200 ${
									(isActive("/discover") || isActive("/")) ? 'opacity-0' : 'opacity-100'
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(255, 255, 0, 0.3))'
								}}
							/>
							<img 
								src="/assets/images/emoji/compass_1f9ed.webp"
								alt="Discover Active"
								className={`w-7 h-7 absolute inset-0 transition-opacity duration-200 ${
									(isActive("/discover") || isActive("/")) ? 'opacity-100' : 'opacity-0'
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(255, 255, 0, 0.3))'
								}}
							/>
						</div>
					} 
					label="Scopri" 
					isActive={isActive("/discover") || isActive("/")} 
					onClick={() => setLocation("/discover")} 
				/>
				{/* Crystal Ball → Wall */}
				<NavButton
					icon={
						<div className="relative w-7 h-7 flex items-center justify-center">
							<span 
								className={`text-2xl emoji-icon emoji-crystal absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
									isActive("/wall") ? 'opacity-0' : 'opacity-100'
								}`}
							>

							</span>
							<img 
								src="/assets/images/staticemo/crystal-ball_1f52e.png"
								alt="Wall"
								className={`w-7 h-7 p-0.5 absolute inset-0 transition-opacity duration-200 ${
									isActive("/wall") ? 'opacity-0' : 'opacity-100'
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(138, 43, 226, 0.3))'
								}}
							/>
							<img 
								src="/assets/images/emoji/crystal-ball_1f52e.webp"
								alt="Wall Active"
								className={`w-7 h-7 absolute inset-0 transition-opacity duration-200 ${
									isActive("/wall") ? 'opacity-100' : 'opacity-0'
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(138, 43, 226, 0.3))'
								}}
							/>
						</div>
					} 
					label="Wall" 
					isActive={isActive("/wall")} 
					onClick={() => setLocation("/wall")} 
				/>

				<NavButton 
					icon={
						<div className="relative w-7 h-7 flex items-center justify-center">
							<span 
								className={`text-2xl emoji-icon emoji-tickets absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
									isActive("/events") ? 'opacity-0' : 'opacity-100'
								}`}
							>

							</span>
							<img 
								src="/assets/images/staticemo/admission-tickets_1f39f-fe0f.png" // Icona statica
								alt="Events"
								className={`w-7 h-7 p-0.5 absolute inset-0 transition-opacity duration-200 ${
									isActive("/events") ? 'opacity-0' : 'opacity-100' // Mostra icona statica quando NON attivo
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.3))'
								}}
							/>
							<img 
								src="/assets/images/emoji/admission-tickets_1f39f-fe0f.webp" // Icona animata
								alt="Events Active"
								className={`w-7 h-7 absolute inset-0 transition-opacity duration-200 ${
									isActive("/events") ? 'opacity-100' : 'opacity-0' // Mostra icona animata quando attivo
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(255, 215, 0, 0.3))'
								}}
							/>
						</div>
					} 
					label="Eventi" 
					isActive={isActive("/events")} 
					onClick={() => setLocation("/events")} 
				/>

				<NavButton 
					icon={
						<div className="relative w-7 h-7 flex items-center justify-center">
							<span 
								className={`text-2xl emoji-icon emoji-groups absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
									isActive("/groups") ? 'opacity-0' : 'opacity-100'
								}`}
							>

							</span>
							<img 
								src="/assets/images/staticemo/performing-arts_1f3ad.png" // Icona statica
								alt="Groups"
								className={`w-7 h-7 p-0.5 absolute inset-0 transition-opacity duration-200 ${
									isActive("/groups") ? 'opacity-0' : 'opacity-100' // Mostra icona statica quando NON attivo
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(156, 39, 176, 0.3))'
								}}
							/>
							<img 
								src="/assets/images/emoji/performing-arts_1f3ad.webp" // Icona animata
								alt="Groups Active"
								className={`w-7 h-7 absolute inset-0 transition-opacity duration-200 ${
									isActive("/groups") ? 'opacity-100' : 'opacity-0' // Mostra icona animata quando attivo
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(156, 39, 176, 0.3))'
								}}
							/>
						</div>
					} 
					label="Gruppi" 
					isActive={isActive("/groups")} 
					onClick={() => setLocation("/groups")} 
				/>

				<NavButton
					icon={
						<div className="relative w-7 h-7 flex items-center justify-center">
							<span
								className={`text-2xl emoji-icon emoji-chat absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
									isActive("/chat") ? 'opacity-0' : 'opacity-100'
								}`}
							>

							</span>
							<img
								src="/assets/images/staticemo/speech-balloon_1f4ac.png" // Icona statica
								alt="Chat"
								className={`w-7 h-7 p-0.5 absolute inset-0 transition-opacity duration-200 ${
									isActive("/chat") ? 'opacity-0' : 'opacity-100' // Mostra icona statica quando NON attivo
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(45, 165, 255, 0.3))'
								}}
							/>
							<img
								src="/assets/images/emoji/speech-balloon_1f4ac.webp" // Icona animata
								alt="Chat Active"
								className={`w-7 h-7 absolute inset-0 transition-opacity duration-200 ${
									isActive("/chat") ? 'opacity-100' : 'opacity-0' // Mostra icona animata quando attivo
								}`}
								style={{
									imageRendering: 'crisp-edges',
									filter: 'drop-shadow(0 0 2px rgba(45, 165, 255, 0.3))'
								}}
							/>
							{hasUnreadMessages && (
								<div
									className="absolute -top-0.5 -right-1 h-3 w-3 bg-red-500 rounded-full z-20 border-2 border-[var(--tg-bg-color,#0c0c0c)]"
									style={{
										pointerEvents: 'none',

									}}
								/>
							)}
						</div>
					}
					label="Chat"
					isActive={isActive("/chat")}
					onClick={handleChatClick}
				/>

				</div>
				</div>
				</div>
			)}

		{/* Animazioni CSS */}
		<style>{`
			@keyframes fadeSlideIn {
				from {
					opacity: 0;
					transform: scale(0.95) translateY(10px);
				}
				to {
					opacity: 1;
					transform: scale(1) translateY(0);
				}
			}

			.animate-fadeSlideIn {
				animation: fadeSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
			}
		`}</style>
		</>
	);
}

interface NavButtonProps {
	icon: React.ReactNode;
	label: string;
	isActive: boolean;
	onClick?: () => void;
}

function NavButton({ icon, label, isActive, onClick }: NavButtonProps) {
	const handleClick = () => {
		// Feedback tattile per navigazione navbar con priorità a Telegram
		try {
			// Prima prova l'API Telegram (priorità alta)
			if (window.Telegram?.WebApp?.HapticFeedback) {
				window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
				console.log('[Navbar] 🔔 Telegram haptic feedback attivato');
			}
			// Fallback a vibrazione browser standard
			else if ('vibrate' in navigator) {
				navigator.vibrate(50); // 50ms di vibrazione
				console.log('[Navbar] 📳 Vibrazione browser attivata');
			}
			// Fallback webkit per compatibilità iOS vecchi
			else if ('webkitVibrate' in navigator) {
				(navigator as any).webkitVibrate(50);
				console.log('[Navbar] 📳 Vibrazione webkit attivata');
			} else {
				console.log('[Navbar] ⚠️ Nessun feedback tattile supportato');
			}
		} catch (error) {
			console.error('[Navbar] ❌ Errore feedback tattile:', error);
		}

		onClick?.();
	};

	return (
		<button 
			className={cn(
				"flex flex-col items-center py-1.5 px-3",
				isActive 
					? "text-[#2565eb] relative after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-1 after:h-1 after:rounded-full after:bg-[#2565eb]" 
					: "text-[#708499]"
			)}
			onClick={handleClick}
		>
			{icon}
		</button>
	);
}
